import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function NotificationBell() {
  const [data, setData] = useState({ count: 0, high: 0, items: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const go = (item) => {
    setOpen(false);
    navigate(item.path || `/m/${item.resource}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" className="relative" data-testid="notification-bell" aria-label="Notifications">
          <Icons.Bell className="h-5 w-5" />
          {data.count > 0 && (
            <span data-testid="notification-count"
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${data.high > 0 ? "bg-destructive" : "bg-primary"}`}>
              {data.count > 9 ? "9+" : data.count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <span className="font-display font-semibold text-sm">Alerts</span>
          <span className="text-xs text-muted-foreground">{data.count} active</span>
        </div>
        <ScrollArea className="max-h-80">
          {data.items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              <Icons.CheckCircle2 className="h-6 w-6 mx-auto mb-2 opacity-40" />
              You're all caught up.
            </div>
          ) : (
            data.items.map((item) => (
              <button key={`${item.type}-${item.id}`} onClick={() => go(item)} data-testid={`notification-item-${item.id}`}
                className="w-full text-left px-4 py-3 border-b last:border-0 hover:bg-secondary transition-colors flex items-start gap-3">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${item.severity === "high" ? "bg-destructive/10 text-destructive" : "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"}`}>
                  {item.type === "followup" ? <Icons.ListTodo className="h-4 w-4" /> : <Icons.CalendarClock className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{item.title || "Untitled"}</div>
                  <div className={`text-xs ${item.severity === "high" ? "text-destructive" : "text-muted-foreground"}`}>{item.subtitle}</div>
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
